import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function canTeach(user) {
  return user?.role === "teacher" || user?.role === "admin";
}

function displayScore(value) {
  return value === null || value === undefined ? "-" : value;
}

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!canTeach(user)) {
      setLoading(false);
      return;
    }
    api.get("/teacher/classes")
      .then(({ data }) => setClasses(data || []))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load teacher dashboard."))
      .finally(() => setLoading(false));
  }, [user]);

  if (!canTeach(user)) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Teacher Dashboard</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">Classes</h1>
            <p className="text-sm text-forge-100/60 max-w-xl">Create a class, share the join code, and monitor students' learning progress.</p>
          </div>
          <Link to="/teacher/classes/new" className="px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold rounded-sm tracking-wide transition-all">
            Create Class
          </Link>
        </div>

        {loading ? (
          <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Loading classes...</div>
        ) : error ? (
          <div className="border border-red-500/30 bg-red-500/10 p-5 text-sm text-red-200">{error}</div>
        ) : classes.length ? (
          <div className="grid md:grid-cols-2 gap-px bg-white/5">
            {classes.map((klass) => (
              <Link key={klass.class_id} to={`/teacher/classes/${klass.class_id}`} className="bg-forge-900 hover:bg-forge-800 p-7 transition-colors">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="font-serif text-2xl text-forge-100 mb-1">{klass.name}</div>
                    <div className="text-xs uppercase tracking-[0.25em] text-forge-100/40">{klass.term || "No term"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">Code</div>
                    <div className="font-mono text-ember-400">{klass.join_code}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Mini label="Students" value={klass.student_count ?? 0} />
                  <Mini label="Avg Score" value={displayScore(klass.avg_reasoning_score)} />
                  <Mini label="Readings" value={klass.reading_completed_count ?? 0} />
                  <Mini label="Debates" value={klass.debate_completed_count ?? 0} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-white/10 p-10 text-center">
            <p className="text-sm text-forge-100/55 mb-5">No classes yet.</p>
            <Link to="/teacher/classes/new" className="inline-block px-5 py-2.5 border border-ember-500/60 hover:border-ember-500 hover:text-ember-400 rounded-sm transition-all">
              Create your pilot class
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="border border-white/10 p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/45 mb-2">{label}</div>
      <div className="font-serif text-3xl text-forge-100">{value}</div>
    </div>
  );
}

function AccessDenied() {
  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Teacher Dashboard</div>
        <h1 className="font-serif text-4xl tracking-tight mb-4">Teacher access required.</h1>
        <p className="text-sm text-forge-100/60">Students can join a class with a professor's code.</p>
        <Link to="/join-class" className="inline-block mt-8 px-5 py-2.5 border border-ember-500/60 hover:border-ember-500 hover:text-ember-400 rounded-sm transition-all">
          Join a Class
        </Link>
      </main>
    </div>
  );
}
